/*
 *   This content is licensed according to the W3C Software License at
 *   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 *
 * Modified Nov 2021 by James Beninger.
 * Original source: https://w3c.github.io/aria-practices/examples/combobox/combobox-select-only.html
 */

'use strict';

window.Plaintext = window.Plaintext = (window.Plaintext || {});

window.Plaintext.StyledDropdown = (function() {
  let dropdownIdx = 0;

  // Save a list of named combobox actions, for future readability
  const SelectActions = {
    Close: 0,
    CloseSelect: 1,
    First: 2,
    Last: 3,
    Next: 4,
    Open: 5,
    PageDown: 6,
    PageUp: 7,
    Previous: 8,
    Select: 9,
    Type: 10,
  };

  /*
   * Helper functions
   */
  
  // filter an array of options against an input string
  // returns an array of options that begin with the filter string, case-independent
  function filterOptions(options = [], filter, exclude = []) {
    return options.filter((option) => {
      const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
      return matches && exclude.indexOf(option) < 0;
    });
  }
  
  // map a key press to an action
  function getActionFromKey(event, menuOpen) {
    const { key, altKey, ctrlKey, metaKey } = event;
    const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']; // all keys that will do the default open action
    // handle opening when closed
    if (!menuOpen && openKeys.includes(key)) {
      return SelectActions.Open;
    }
  
    // home and end move the selected option when open or closed
    if (key === 'Home') {
      return SelectActions.First;
    }
    if (key === 'End') {
      return SelectActions.Last;
    }
  
    // handle typing characters when open or closed
    if (
      key === 'Backspace' ||
      key === 'Clear' ||
      (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
    ) {
      return SelectActions.Type;
    }
  
    // handle keys when open
    if (menuOpen) {
      if (key === 'ArrowUp' && altKey) {
        return SelectActions.CloseSelect;
      } else if (key === 'ArrowDown' && !altKey) {
        return SelectActions.Next;
      } else if (key === 'ArrowUp') {
        return SelectActions.Previous;
      } else if (key === 'PageUp') {
        return SelectActions.PageUp;
      } else if (key === 'PageDown') {
        return SelectActions.PageDown;
      } else if (key === 'Escape') {
        return SelectActions.Close;
      } else if (key === 'Enter' || key === ' ') {
        return SelectActions.CloseSelect;
      }
    }
  }
  
  // return the index of an option from an array of options, based on a search string
  // if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
  function getIndexByLetter(options, filter, startIndex = 0) {
    const orderedOptions = [
      ...options.slice(startIndex),
      ...options.slice(0, startIndex),
    ];
    const firstMatch = filterOptions(orderedOptions, filter)[0];
    const allSameLetter = (array) => array.every((letter) => letter === array[0]);
  
    // first check if there is an exact match for the typed string
    if (firstMatch) {
      return options.indexOf(firstMatch);
    }
  
    // if the same letter is being repeated, cycle through first-letter matches
    else if (allSameLetter(filter.split(''))) {
      const matches = filterOptions(orderedOptions, filter[0]);
      return options.indexOf(matches[0]);
    }
  
    // if no matches, return -1
    else {
      return -1;
    }
  }

  // get an updated option index after performing an action
  function getUpdatedIndex(currentIndex, maxIndex, action) {
    const pageSize = 10; // used for pageup/pagedown
  
    switch (action) {
      case SelectActions.First:
        return 0;
      case SelectActions.Last:
        return maxIndex;
      case SelectActions.Previous:
        return Math.max(0, currentIndex - 1);
      case SelectActions.Next:
        return Math.min(maxIndex, currentIndex + 1);
      case SelectActions.PageUp:
        return Math.max(0, currentIndex - pageSize);
      case SelectActions.PageDown:
        return Math.min(maxIndex, currentIndex + pageSize);
      default:
        return currentIndex;
    }
  }
  
  // check if an element is currently scrollable
  function isScrollable(element) {
    return element && element.clientHeight < element.scrollHeight;
  }

  // ensure a given child element is within the parent's visible scroll area
  // if the child is not visible, scroll the parent
  function maintainScrollVisibility(activeElement, scrollParent) {
    const { offsetHeight, offsetTop } = activeElement;
    const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;
  
    const isAbove = offsetTop < scrollTop;
    const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;
  
    if (isAbove) {
      scrollParent.scrollTo(0, offsetTop);
    } else if (isBelow) {
      scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
    }
  }
  
  /*
   * Select Component
   * Accepts a combobox element  
   */
  const Select = function (selectEl, className = '') {
    this.dropdownIdx = dropdownIdx++;
    this.idBase = selectEl.id || `pt-dropdown-${ this.dropdownIdx }`;
    
    // element refs
    const el = this.wrapSelectEl(selectEl, className);
    this.el = el;
    this.comboEl = el.querySelector('[role=combobox]');
    this.listboxEl = el.querySelector('[role=listbox]');
    this.selectEl = el.querySelector('select');
    
    const selectedOption = this.selectEl.options[this.selectEl.selectedIndex || 0];
    const inputValue = (selectedOption && selectedOption.value) || '';
    
    // When the select is focused, focus the combo instead.  This can happen during form
    // validation with required elements
    this.selectEl.addEventListener('focus', () => this.comboEl.focus());

    // data
    this.options = [];

    // state
    this.activeIndex = 0;
    this.open = false;
    this.searchString = '';
    this.searchTimeout = null;
    this.selectEl.tabIndex = -1;

    this.copyOptionsFromSelect();
    this.augmentOptions();
  
    this.setValue(inputValue, true);
  
    // init
    if (el && this.comboEl && this.listboxEl) {
      this.init();
    }

    selectEl.styledDropdown = this;
    this.isInitialized = true;
  };
  
  Select.prototype.wrapSelectEl = function(selectEl, className) {
    const selectId = selectEl.id;
    const comboId = this.idBase + '__combo';
    const listId = this.idBase + '__list';

    this.labelId = '';
    this.ariaLabel = selectEl.getAttribute('aria-label') || null;
    
    // If we have an ID, try to find the label for this select.  Assign the label
    // an ID if it doesn't have one so we can reference it with aria-labelledby
    if (selectId) {
      const labelEl = document.querySelector(`label[for="${ selectId }"]`);
      
      if (labelEl) {
        if (!labelEl.id) {
          labelEl.id = this.idBase + '__label';
        }

        this.labelId = labelEl.id;
      }
    }
    
    // Remove the select from the tab order
    selectEl.tabIndex = -1;
    
    // Create the wrapped element
    const wrap = document.createElement('div');
    wrap.classList.add('pt-dropdown__wrap');
    
    if (className) {
      wrap.classList.add(className);
    }
    
    wrap.innerHTML = `
      <div id="${ comboId }"
        aria-controls="${ listId }"
        aria-expanded="false"
        aria-haspopup="listbox"
        aria-labelledby="${ this.labelId || '' }"
        class="pt-dropdown__combo"
        role="combobox"
        tabindex="0">
      </div>
      <div class="pt-dropdown__list"
           role="listbox"
           id="${ listId }"
            aria-labelledby="${ this.labelId || '' }"
           tabindex="-1">
      </div>`;
    
    
    // Insert the wrap before the select, then pull the select inside the wrap
    selectEl.parentNode.insertBefore(wrap, selectEl);
    wrap.appendChild(selectEl);
    
    return wrap;
  };

  Select.prototype.init = function () {
    // add event listeners
    this.comboEl.addEventListener('blur', this.onComboBlur.bind(this));
    this.comboEl.addEventListener('click', this.onComboClick.bind(this));
    this.comboEl.addEventListener('keydown', this.onComboKeyDown.bind(this));
  };
  
  Select.prototype.copyOptionsFromSelect = function() {
    // Clear any existing options
    this.listboxEl.innerHTML = '';

    this.selectEl.querySelectorAll('option').forEach((optionEl, idx) => {
      const listboxOptionEl = this.createOption(optionEl.innerText, idx, optionEl.value);
      this.listboxEl.appendChild(listboxOptionEl);
    });

    this.augmentOptions();
    this.comboEl.setAttribute('aria-required', this.selectEl.hasAttribute('required') ? 'true' : 'false');
  }
  
  /**
   * Sets IDs, attributes, and event listeners of options
   */
  Select.prototype.augmentOptions = function () {
    const optionEls = this.listboxEl.querySelectorAll('[role=option]');
    const idx = 0;

    optionEls.forEach((o, idx) => {
      const selected = (idx === this.activeIndex);
      const id = `${this.idBase}__option${idx}`;
      
      o.id = id;
      o.setAttribute('aria-selected', selected);
      o.className = selected ? 'pt-dropdown__option option-current' : 'pt-dropdown__option';
      
      const pt = o.Plaintext = (o.Plaintext || {});
      
      pt.ddClickListener && o.removeEventListener('click', pt.ddClickListener);
      pt.ddClickListener = (event) => {
        event.stopPropagation();
        this.onOptionClick(idx);
      };
      o.addEventListener('click', pt.ddClickListener);
      
      pt.ddMouseDownListener && o.removeEventListener('mousedown', pt.ddMouseDownListener);
      pt.ddMouseDownListener = this.onOptionMouseDown.bind(this);
      o.addEventListener('mousedown', pt.ddMouseDownListener);
      
      this.options[idx] = o.innerText;
    });
  };

  
  /**
   * @param newValue string label of option being set
   * @param setValueByVal boolean (default false) if true, set the value by the <option> value rather than its label
   */
  Select.prototype.setValue = function(newValue, setValueByVal = false) {
    let optionValue = null;
    let selectedIdx = 0;
    let idx = 0;
    let displayedValue = newValue;
    let selectedOptionEl = null;
    
    this.listboxEl.querySelectorAll('[role=option]').forEach(optionEl =>{
      const label = optionEl.innerText;
      const val = (optionEl.dataset.naDropdownValue === null) ? label : optionEl.dataset.naDropdownValue;
      const matches = (setValueByVal ? (newValue === val) : (newValue === label));
      
      if (matches) {
        selectedIdx = idx;
        displayedValue = label;
        optionValue = val;
        selectedOptionEl = optionEl;
        return;
      }
      
      idx++;
    });

    const changed = (this.selectEl.selectedIndex !== selectedIdx);
    
    this.selectEl.selectedIndex = selectedIdx;
    this.comboEl.innerText = displayedValue;
    this.comboEl.setAttribute('aria-labelledby', `${ this.labelId } ${ selectedOptionEl.id }`);
    
    if (this.ariaLabel) {
      this.comboEl.setAttribute('aria-label', this.ariaLabel + ': ' + displayedValue);
    }

    if (this.isInitialized) {
      const event = new CustomEvent('change', { detail: { newLabel: displayedValue, newValue: optionValue }});
      this.selectEl.dispatchEvent(event);
    }
  };

  Select.prototype.createOption = function (optionText, index, value = null) {
    const optionEl = document.createElement('div');
    optionEl.setAttribute('role', 'option');
    optionEl.innerText = optionText;
    
    if (value !== null) {
      optionEl.dataset.naDropdownValue = value;
    }
    
    return optionEl;
  };

  Select.prototype.getSearchString = function (char) {
    // reset typing timeout and start new timeout
    // this allows us to make multiple-letter matches, like a native select
    if (typeof this.searchTimeout === 'number') {
      window.clearTimeout(this.searchTimeout);
    }
  
    this.searchTimeout = window.setTimeout(() => {
      this.searchString = '';
    }, 500);
  
    // add most recent letter to saved search string
    this.searchString += char;
    return this.searchString;
  };

  Select.prototype.onComboBlur = function () {
    // do not do blur action if ignoreBlur flag has been set
    if (this.ignoreBlur) {
      this.ignoreBlur = false;
      return;
    }
  
    // select current option and close
    if (this.open) {
      this.selectOption(this.activeIndex);
      this.updateMenuState(false, false);
    }
  };
  
  Select.prototype.onComboClick = function () {
    this.updateMenuState(!this.open, false);
  };
  
  Select.prototype.onComboKeyDown = function (event) {
    const { key } = event;
    const max = this.options.length - 1;
  
    const action = getActionFromKey(event, this.open);
  
    switch (action) {
      case SelectActions.Last:
      case SelectActions.First:
        this.updateMenuState(true);
      // intentional fallthrough
      case SelectActions.Next:
      case SelectActions.Previous:
      case SelectActions.PageUp:
      case SelectActions.PageDown:
        event.preventDefault();
        this.el.classList.add('pt-dropdown__wrap--withKeys');
      
        return this.onOptionChange(
          getUpdatedIndex(this.activeIndex, max, action)
        );
      case SelectActions.CloseSelect:
        event.preventDefault();
        this.selectOption(this.activeIndex);
      // intentional fallthrough
      case SelectActions.Close:
        event.preventDefault();
        return this.updateMenuState(false);
      case SelectActions.Type:
        return this.onComboType(key);
      case SelectActions.Open:
        event.preventDefault();
        return this.updateMenuState(true);
    }
  };
  
  Select.prototype.onComboType = function (letter) {
    // open the listbox if it is closed
    this.updateMenuState(true);
  
    // find the index of the first matching option
    const searchString = this.getSearchString(letter);
    const searchIndex = getIndexByLetter(
      this.options,
      searchString,
      this.activeIndex + 1
    );
  
    // if a match was found, go to it
    if (searchIndex >= 0) {
      this.onOptionChange(searchIndex);
    }
    // if no matches, clear the timeout and search string
    else {
      window.clearTimeout(this.searchTimeout);
      this.searchString = '';
    }
  };
  
  Select.prototype.onOptionChange = function (index) {
    // update state
    this.activeIndex = index;
  
    // update aria-activedescendant
    this.comboEl.setAttribute('aria-activedescendant', `${this.idBase}__option${index}`);
  
    // update active option styles
    const options = this.el.querySelectorAll('[role=option]');
    [...options].forEach((optionEl) => {
      optionEl.classList.remove('option-current');
    });
    options[index].classList.add('option-current');
  
    // ensure the new option is in view
    if (isScrollable(this.listboxEl)) {
      maintainScrollVisibility(options[index], this.listboxEl);
    }
  };
  
  Select.prototype.onOptionClick = function (index) {
    this.onOptionChange(index);
    this.selectOption(index);
    this.updateMenuState(false);
  };
  
  Select.prototype.onOptionMouseDown = function () {
    // Clicking an option will cause a blur event,
    // but we don't want to perform the default keyboard blur action
    this.ignoreBlur = true;
  };
  
  Select.prototype.selectOption = function (index) {
    // update state
    this.activeIndex = index;

    // update displayed value
    const selected = this.options[index];
    this.setValue(selected);
  
    // update aria-selected
    const options = this.el.querySelectorAll('[role=option]');
    [...options].forEach((optionEl) => {
      optionEl.setAttribute('aria-selected', 'false');
    });
    options[index].setAttribute('aria-selected', 'true');
  };
  
  Select.prototype.updateMenuState = function (open, callFocus = true) {
    if (this.open === open) {
      return;
    }
  
    // update state
    this.open = open;
  
    // update aria-expanded and styles
    this.comboEl.setAttribute('aria-expanded', `${open}`);
    
    if (open) {
      this.el.classList.remove('pt-dropdown__wrap--withKeys');
      this.el.classList.add('pt-dropdown__wrap--open');
    }
    else {
      this.el.classList.remove('pt-dropdown__wrap--open');
    }
  
    // update activedescendant
    const activeID = open ? `${this.idBase}__option${this.activeIndex}` : '';
    this.comboEl.setAttribute('aria-activedescendant', activeID);
  
    // move focus back to the combobox, if needed
    callFocus && this.comboEl.focus();
  };

  function init(selectEl, className = null) {
    if (selectEl.tagName !== 'SELECT') {
      console.error("Element must be <select>.", selectEl);
      return;
    }
    
    className = className || selectEl.dataset.naDropdownClass;
    
    return new Select(selectEl, className);
  }
  
  function initAll(selector = '.pt-dropdown__wrap', className = '') {
    document.querySelectorAll(selector).forEach(el => {
      init(el);
    });
  }

  return {
    init,
    initAll
  }
})();